// Copyright (c) 2024-2026 AlphaGrade. All Rights Reserved.
// Proprietary and confidential. See LICENSE file in project root.
import React, { useEffect, useCallback, useState, useRef, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import { PortfolioProvider, usePortfolio } from './context/PortfolioContext';
import { ThemeProvider } from './context/ThemeContext';
import { TierProvider, useTier } from './context/TierContext';
import { GradeWatchProvider } from './context/GradeWatchContext';
import UpgradePrompt from './components/UpgradePrompt';
import DemoButton from './components/demos/DemoButton';
import Navigation from './components/Navigation';
import PublicTopBar from './components/PublicTopBar';
import { isAuthenticated } from './utils/auth';
import { applyTitle } from './utils/pageTitle';
import CSVUpload from './components/CSVUpload';
import PortfolioIntro from './components/portfolio/PortfolioIntro';
import WeeklyDigestToggle from './components/WeeklyDigestToggle';
import PortfolioEditor from './components/portfolio/PortfolioEditor';
import PortfolioTable from './components/PortfolioTableEnhanced';
import Recommendations from './components/Recommendations';
import CopilotChat from './components/CopilotChat';
import LoginGate, { OPEN_APP_ROUTES } from './components/LoginGate';
import { convictionColor } from './utils/convictionLabel';
import NewsLink from './components/news/NewsLink';
import LandingPage from './components/LandingPage';
import Onboarding from './components/Onboarding';
import LimitReachedModal from './components/LimitReachedModal';
import InstallPrompt from './components/InstallPrompt';
import UpgradeConfirmer from './components/UpgradeConfirmer';
import RegimeIndicator from './components/portfolio/RegimeIndicator';
import PortfolioQualityScore from './components/portfolio/PortfolioQualityScore';
import ConvictionAlerts from './components/portfolio/ConvictionAlerts';
import PortfolioDashboard from './components/portfolio/PortfolioDashboard';
import TradeSimulator from './components/portfolio/TradeSimulator';
import PortfolioXRay from './components/portfolio/PortfolioXRay';
import RiskAlertBanner from './components/portfolio/RiskAlertBanner';
import KPICards from './components/portfolio/KPICards';
import PortfolioHeatmap from './components/portfolio/PortfolioHeatmap';
import HoldingsInsight from './components/portfolio/HoldingsInsight';
import DividendTracker from './components/portfolio/DividendTracker';
import ETFOverlap from './components/portfolio/ETFOverlap';
import { SkeletonPortfolioTable, SkeletonDashboard, SkeletonScores, SkeletonCard } from './components/Skeleton';
import './components/MacroEnergy.css';

// Route-level code splitting. The whole app was one 1.93MB bundle, so every
// anonymous visitor downloaded and parsed the admin pages, the Alpha
// workspace and every charting library before the landing page could become
// interactive. These are the routes a cold visitor never opens.
// LandingPage is deliberately NOT lazy: it is the page ads land on, and
// adding a round trip there would trade the win for the thing we care about.
const GradeWatchPage = React.lazy(() => import('./components/GradeWatchPage'));
const WatchlistsPage = React.lazy(() => import('./components/WatchlistsPage'));
const NewsPage = React.lazy(() => import('./components/news/NewsPage'));
const ModelPortfoliosPage = React.lazy(() => import('./components/modelportfolio/ModelPortfoliosPage'));
const StockScreener = React.lazy(() => import('./components/StockScreener'));
const MarketResearchPage = React.lazy(() => import('./components/MarketResearchPage'));
const MarketTrends = React.lazy(() => import('./components/MarketTrends'));
const CorrelationsPage = React.lazy(() => import('./components/CorrelationsPage'));
const MacroEnergy = React.lazy(() => import('./components/MacroEnergy'));
const PricingPage = React.lazy(() => import('./components/PricingPage'));
const Glossary = React.lazy(() => import('./components/Glossary'));
const FAQPage = React.lazy(() => import('./components/FAQPage'));
const ContactPage = React.lazy(() => import('./components/ContactPage'));
const HowScoresWork = React.lazy(() => import('./components/HowScoresWork'));
const AlphaMemory = React.lazy(() => import('./components/AlphaMemory'));
const AlphaWorkspace = React.lazy(() => import('./components/AlphaWorkspace'));
const AdminAttribution = React.lazy(() => import('./components/AdminAttribution'));
const AdminSocial = React.lazy(() => import('./components/AdminSocial'));
const AdminToolsPage = React.lazy(() => import('./components/AdminToolsPage'));
// Recharts again: only the portfolio page draws this one.
const BenchmarkComparison = React.lazy(() => import('./components/portfolio/BenchmarkComparison'));


// Progress Bar Component
function ProgressBar({ progress }) {
  if (!progress || progress.status === 'idle') return null;
  
  const percent = progress.total > 0 
    ? Math.round((progress.completed / progress.total) * 100)
    : 0;
  
  return (
    <div className="progress-container">
      <div className="progress-bar">
        <div 
          className="progress-fill" 
          style={{ width: `${percent}%` }}
        />
      </div>
      <div className="progress-text">
        Analyzing {progress.completed} of {progress.total} positions... 
        ({progress.elapsed_seconds}s elapsed)
      </div>
      <div className="progress-details">
        {Object.entries(progress.positions || {}).map(([symbol, status]) => (
          <span key={symbol} className={`position-status ${status}`}>
            {symbol}: {status}
          </span>
        ))}
      </div>
    </div>
  );
}

// Portfolio Page Component - Uses Context for persistence
function PortfolioPage() {
  const {
    portfolioItems,
    setPortfolioItems,
    analysisResult,
    scores,
    recommendations,
    loading,
    setLoading,
    progress,
    setProgress,
    lastAnalyzed,
    storeAnalysisResult,
    clearPortfolio,
  } = usePortfolio();

  const { hasFeature, portfolio_positions } = useTier();

  // Feature states
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [savedPortfolios, setSavedPortfolios] = useState([]);
  const [compareTarget, setCompareTarget] = useState(null);
  const [saveName, setSaveName] = useState('');
  const [newsData, setNewsData] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [scoreHistory, setScoreHistory] = useState([]);
  const [featureToast, setFeatureToast] = useState(null);
  const toastTimer = useRef(null);

  const showToast = useCallback((message, type = 'success') => {
    setFeatureToast({ message, type });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setFeatureToast(null), 3000);
  }, []);

  // Load saved portfolios and score history on mount
  useEffect(() => {
    const sp = localStorage.getItem('alphagrade_saved_portfolios');
    if (sp) { try { setSavedPortfolios(JSON.parse(sp)); } catch(e) {} }
    const sh = localStorage.getItem('alphagrade_score_history');
    if (sh) { try { setScoreHistory(JSON.parse(sh)); } catch(e) {} }
  }, []);

  // Auto-save score history when analysis completes
  useEffect(() => {
    if (!analysisResult || loading) return;
    const entry = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      totalConviction: analysisResult.total_conviction,
      regime: analysisResult.regime,
      positions: analysisResult.positions_processed,
      scores: scores?.slice(0, 10).map(s => ({
        symbol: s.symbol,
        conviction: s.combined_conviction
      }))
    };
    setScoreHistory(prev => {
      const updated = [entry, ...prev.filter(h =>
        new Date(h.timestamp).toDateString() !== new Date().toDateString()
      )].slice(0, 30);
      localStorage.setItem('alphagrade_score_history', JSON.stringify(updated));
      return updated;
    });
  }, [analysisResult, loading, scores]);

  // Ref-based settings action handler
  const settingsRef = useRef();
  settingsRef.current = (actionId) => {
    switch (actionId) {
      case 'export-pdf':
        window.print();
        break;
      case 'share-card': {
        if (!analysisResult) {
          showToast('Analyze a portfolio first', 'warning');
          return;
        }
        const lines = ['AlphaGrade - Portfolio Analysis', ''];
        lines.push(`Total Conviction: ${analysisResult.total_conviction?.toFixed(1) ?? '-'}/5`);
        lines.push(`Market Regime: ${analysisResult.regime || 'Unknown'}`);
        lines.push(`Positions: ${analysisResult.positions_processed}`);
        lines.push('');
        if (scores?.length) {
          scores.slice(0, 10).forEach(s => {
            lines.push(`${s.symbol}: ${s.combined_conviction?.toFixed(1) ?? '-'}/5`);
          });
        }
        lines.push('', `Analyzed at ${lastAnalyzed?.toLocaleString() || 'N/A'}`);
        navigator.clipboard.writeText(lines.join('\n')).then(() => {
          showToast('Score card copied to clipboard!');
        }).catch(() => {
          showToast('Failed to copy', 'error');
        });
        break;
      }
      case 'compare':
        if (!analysisResult) {
          showToast('Analyze a portfolio first', 'warning');
          return;
        }
        setShowCompareModal(true);
        break;
      case 'stock-news': {
        if (!portfolioItems?.length) {
          showToast('Upload a portfolio first', 'warning');
          return;
        }
        setShowNewsModal(true);
        setNewsLoading(true);
        const symbols = portfolioItems.map(p => p.symbol).slice(0, 5).join(',');
        fetch(`/api/market/news?symbols=${symbols}`)
          .then(r => r.ok ? r.json() : [])
          .then(data => setNewsData(Array.isArray(data) ? data.slice(0, 15) : data.articles?.slice(0, 15) || []))
          .catch(() => setNewsData([]))
          .finally(() => setNewsLoading(false));
        break;
      }
      case 'score-history':
        setShowHistoryModal(true);
        break;
      default: break;
    }
  };

  useEffect(() => {
    const handler = (e) => settingsRef.current?.(e.detail);
    window.addEventListener('settings-action', handler);
    return () => window.removeEventListener('settings-action', handler);
  }, []);

  const handleSavePortfolio = () => {
    if (!saveName.trim() || !analysisResult) return;
    const entry = {
      name: saveName.trim(),
      timestamp: new Date().toISOString(),
      totalConviction: analysisResult.total_conviction,
      regime: analysisResult.regime,
      positions: portfolioItems.map(p => p.symbol),
      scores: scores?.map(s => ({ symbol: s.symbol, conviction: s.combined_conviction }))
    };
    const updated = [entry, ...savedPortfolios.filter(p => p.name !== entry.name)].slice(0, 10);
    localStorage.setItem('alphagrade_saved_portfolios', JSON.stringify(updated));
    setSavedPortfolios(updated);
    setSaveName('');
    showToast(`Portfolio "${entry.name}" saved`);
  };

  const handleDeleteSavedPortfolio = (name, e) => {
    e.stopPropagation();
    const updated = savedPortfolios.filter(p => p.name !== name);
    localStorage.setItem('alphagrade_saved_portfolios', JSON.stringify(updated));
    setSavedPortfolios(updated);
    if (compareTarget?.name === name) setCompareTarget(null);
  };

  // Poll for progress updates during analysis
  const pollProgress = useCallback(async () => {
    try {
      const response = await fetch('/api/portfolio/snapshot/progress');
      if (response.ok) {
        const data = await response.json();
        setProgress(data);
        return data.status === 'processing';
      }
    } catch (e) {
      console.log('Progress polling error:', e);
    }
    return false;
  }, [setProgress]);

  useEffect(() => {
    let interval;
    if (loading) {
      interval = setInterval(async () => {
        const stillProcessing = await pollProgress();
        if (!stillProcessing && progress?.status === 'processing') {
          setProgress(prev => ({ ...prev, status: 'idle' }));
        }
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [loading, pollProgress, progress?.status, setProgress]);

  const handlePortfolioUpload = async (items) => {
    setPortfolioItems(items);
  };

  const handleCalculateScores = async (items, extras = {}) => {
    // Check position limit
    if (items.length > portfolio_positions) {
      alert(`Your plan supports up to ${portfolio_positions} positions. You submitted ${items.length}. Please upgrade your plan or reduce positions.`);
      return;
    }

    // Value-only "other holdings": cash + precious metals (never scored).
    const cash = Number(extras.cash) > 0 ? Number(extras.cash) : 0;
    const metals = Array.isArray(extras.metals)
      ? extras.metals.filter(m => m && m.metal && Number(m.ounces) > 0)
                     .map(m => ({ metal: m.metal, ounces: Number(m.ounces) }))
      : [];

    setLoading(true);
    setProgress({ status: 'processing', total: items.length, completed: 0, elapsed_seconds: 0, positions: {} });

    try {
      // Use the FAST concurrent snapshot endpoint for better performance.
      // Send the auth token so the backend recognizes the logged-in user and
      // loads their SAVED cash/metals (otherwise the analysis is anonymous and
      // the composition drops cash + precious metals to $0).
      const authToken = localStorage.getItem('alphagrade_auth_token');
      const response = await fetch('/api/portfolio/snapshot/fast', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({
          positions: items.map(item => ({
            symbol: item.symbol,
            shares: item.shares,
            entry_price: item.entry_price
          })),
          cash,
          metals,
          include_crypto: true
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to analyze portfolio');
      }
      
      const data = await response.json();
      
      // Store in context (persists across navigation)
      storeAnalysisResult(data, items);
      
      // Show success message with timing info
      if (data.processing_time_seconds) {
        console.log(`✅ Portfolio analyzed in ${data.processing_time_seconds}s`);
      }
    } catch (error) {
      console.error('Error analyzing portfolio:', error);
      alert('Error analyzing portfolio: ' + error.message);
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  return (
    <div className="App-main">
      <DemoButton type="portfolio" showPopup={true} />
      {/* Explain the page while it is empty. See PortfolioIntro.js: only 2 of 40
          users had ever built a portfolio, and nothing on this page said what
          building one actually gets you. */}
      {portfolioItems.length === 0 && !loading && <PortfolioIntro />}
      {portfolioItems.length > 0 && <NewsLink label="Latest news on your holdings" />}
      <PortfolioEditor
        onSaved={(positions) => {
          // Mirror the saved DB positions into the analysis context so the
          // existing CSV/analyze flow stays in sync with what Alpha sees.
          setPortfolioItems(positions.map((p) => ({
            symbol: p.symbol,
            shares: p.shares,
            entry_price: p.entry_price,
          })));
        }}
        onAnalyze={(items) => {
          // Mirror into context first so the table renders against the
          // same set we're scoring, then kick off the same fast-snapshot
          // path the CSV/builder flow uses.
          setPortfolioItems(items);
          handleCalculateScores(items);
        }}
      />
      <CSVUpload
        onUpload={handlePortfolioUpload}
        onCalculateScores={handleCalculateScores}
        loading={loading}
        onClear={clearPortfolio}
      />

      <WeeklyDigestToggle />

      {loading && <ProgressBar progress={progress} />}

      {loading && portfolioItems.length > 0 && (
        <>
          <SkeletonScores count={5} />
          <SkeletonDashboard />
          <SkeletonPortfolioTable rows={Math.min(portfolioItems.length, 8)} />
        </>
      )}

      {analysisResult && !loading && (
        <RegimeIndicator regime={analysisResult.regime} turbulence={analysisResult.turbulence} />
      )}

      {analysisResult && !loading && (
        <div className="analysis-summary">
          <h3>Portfolio Analysis Complete</h3>
          <div className="summary-stats">
            <div className="stat">
              <span className="stat-label">Total Conviction</span>
              <span className="stat-value">{analysisResult.total_conviction?.toFixed(1) || 'N/A'}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Market Regime</span>
              <span className="stat-value">{analysisResult.regime || 'Unknown'}</span>
            </div>
            {analysisResult.turbulence && (
              <div className="stat">
                <span className="stat-label">Turbulence</span>
                <span className="stat-value" style={{
                  color: analysisResult.turbulence.turbulence_label === 'Calm' ? '#22c55e'
                    : analysisResult.turbulence.turbulence_label === 'Choppy' ? '#eab308'
                    : analysisResult.turbulence.turbulence_label === 'Turbulent' ? '#f97316'
                    : '#ef4444'
                }}>
                  {analysisResult.turbulence.turbulence_label} ({(analysisResult.turbulence.dampener * 100).toFixed(0)}%)
                </span>
              </div>
            )}
            <div className="stat">
              <span className="stat-label">Analysis Time</span>
              <span className="stat-value">{analysisResult.processing_time_seconds}s</span>
            </div>
            <div className="stat">
              <span className="stat-label">Positions</span>
              <span className="stat-value">{analysisResult.positions_processed}</span>
            </div>
            {analysisResult.total_portfolio_value > 0 && (
              <div className="stat">
                <span className="stat-label">Total Value</span>
                <span className="stat-value">${Math.round(analysisResult.total_portfolio_value).toLocaleString()}</span>
              </div>
            )}
            {lastAnalyzed && (
              <div className="stat">
                <span className="stat-label">Last Updated</span>
                <span className="stat-value stat-timestamp">{lastAnalyzed.toLocaleTimeString()}</span>
              </div>
            )}
          </div>
          {(() => {
            // Explain WHY conviction/action looks capped when the macro backdrop
            // is dampening scores — otherwise users are confused how a stock can
            // be strong (blue) on Quality + Setup yet only read Neutral.
            const regimeRaw = (analysisResult.regime || '').toString().toLowerCase();
            const isRiskOn = regimeRaw.includes('risk_on') || regimeRaw.includes('risk-on') || regimeRaw.includes('riskon');
            const turb = analysisResult.turbulence;
            const dampened = turb && typeof turb.dampener === 'number' && turb.dampener < 1.0;
            if (isRiskOn && !dampened) return null;
            return (
              <div className="market-context-note">
                <span className="mcn-icon" aria-hidden="true">🌐</span>
                <div className="mcn-body">
                  <strong>Why strong names may read “Neutral” right now:</strong> A stock’s overall
                  conviction blends its Quality and Setup with its Convex (upside) and the macro
                  backdrop — and right now a <strong>{analysisResult.regime || 'mixed'}</strong> regime
                  {turb?.turbulence_label ? <> with <strong>{turb.turbulence_label}</strong> conditions</> : null}
                  {' '}is pulling Setup &amp; Convex down across <em>every</em> holding. So even names that
                  score strong (blue) on Quality and Setup can land at Neutral. This reflects the market
                  environment, not the individual company — expect strong names to re-rate higher as
                  conditions calm. Open any position for its macro &amp; turbulence detail.
                </div>
              </div>
            );
          })()}
          {analysisResult.allocation && analysisResult.total_portfolio_value > 0 && (() => {
            const a = analysisResult.allocation;
            const segs = [
              { key: 'equities', label: 'Equities', color: '#3b82f6' },
              { key: 'crypto', label: 'Digital Assets', color: '#a855f7' },
              { key: 'metals', label: 'Precious Metals', color: '#eab308' },
              { key: 'cash', label: 'Cash', color: '#22c55e' },
            ].filter(s => a[s.key] && a[s.key].value > 0);
            if (!segs.length) return null;
            return (
              <div className="composition-block">
                <div className="composition-title">Portfolio Composition</div>
                <div className="composition-bar">
                  {segs.map(s => (
                    <div
                      key={s.key}
                      className="composition-seg"
                      style={{ width: `${a[s.key].pct}%`, background: s.color }}
                      title={`${s.label}: ${a[s.key].pct}%`}
                    />
                  ))}
                </div>
                <div className="composition-legend">
                  {segs.map(s => (
                    <span key={s.key} className="composition-item">
                      <span className="composition-dot" style={{ background: s.color }} />
                      {s.label} — ${Math.round(a[s.key].value).toLocaleString()} ({a[s.key].pct}%)
                    </span>
                  ))}
                </div>
                {analysisResult.allocation.cash?.pct >= 20 && (
                  <div className="composition-note">
                    💰 {analysisResult.allocation.cash.pct}% in cash — meaningful dry powder for deploying into opportunities.
                  </div>
                )}
              </div>
            );
          })()}
          {analysisResult.errors && analysisResult.errors.length > 0 && (
            <div className="analysis-warnings">
              <strong>⚠️ Warnings:</strong>
              <ul>
                {analysisResult.errors.slice(0, 3).map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ── Core Analysis Results ── */}
      {/* Holdings treemap first: before any score or rollup, show where the
          money actually is. Sized by position value, so the tiles that dominate
          are the ones whose moves actually matter to him. */}
      {analysisResult && !loading && portfolioItems.length > 0 && (
        <PortfolioHeatmap items={portfolioItems} scores={scores} lastAnalyzed={lastAnalyzed} />
      )}

      {analysisResult && !loading && (
        <KPICards portfolioItems={portfolioItems} scores={scores} analysisResult={analysisResult} />
      )}

      {analysisResult && !loading && hasFeature('ai_chat') && (
        <div className="ai-teaser-line">
          Want to spot stretched valuations, identify potential value traps, or find your strongest peer-relative buys?
          {' '}
          <span className="ai-teaser-pointer">Ask Alpha &rarr;</span>
        </div>
      )}

      {analysisResult && !loading && (
        <PortfolioQualityScore items={portfolioItems} scores={scores} analysisResult={analysisResult} />
      )}

      <ConvictionAlerts />

      {analysisResult && !loading && scores?.length > 0 && (
        <RiskAlertBanner scores={scores} />
      )}

      {portfolioItems.length > 0 && (
        <PortfolioTable
          items={portfolioItems}
          scores={scores}
          loading={loading}
          analysisResult={analysisResult}
        />
      )}

      {/* ── Portfolio Context & Enrichment ── */}
      {analysisResult && !loading && scores?.length > 0 && (
        <PortfolioDashboard scores={scores} />
      )}

      {analysisResult && !loading && (
        hasFeature('benchmark') || hasFeature('benchmark_basic')
          ? (
            <Suspense fallback={<div aria-busy="true" />}>
              <BenchmarkComparison portfolioItems={portfolioItems} />
            </Suspense>
          )
          : <UpgradePrompt feature="benchmark" />
      )}

      {analysisResult && !loading && (
        hasFeature('holdings_full') || hasFeature('holdings_pnl')
          ? (
            <HoldingsInsight portfolioItems={portfolioItems} analysisResult={analysisResult} />
          )
          : <UpgradePrompt feature="holdings_full" />
      )}

      {analysisResult && !loading && (
        hasFeature('dividend_tracker')
          ? (
            <DividendTracker portfolioItems={portfolioItems} />
          )
          : <UpgradePrompt feature="dividend_tracker" />
      )}

      {analysisResult && !loading && (
        hasFeature('etf_overlap')
          ? (
            <ETFOverlap portfolioItems={portfolioItems} />
          )
          : <UpgradePrompt feature="etf_overlap" />
      )}

      {analysisResult && !loading && scores?.length > 0 && (
        <PortfolioXRay scores={scores} />
      )}

      {/* ── Action Items ── */}
      {recommendations.length > 0 && (
        <Recommendations recommendations={recommendations} />
      )}

      {analysisResult && !loading && scores?.length > 0 && (
        hasFeature('trade_simulator')
          ? (
            <TradeSimulator scores={scores} analysisResult={analysisResult} />
          )
          : <UpgradePrompt feature="trade_simulator" />
      )}

      {/* Show persistence notice when data exists */}
      {analysisResult && !loading && (
        <div className="persistence-notice">
          Your analysis is saved and will persist when you navigate to other tabs
        </div>
      )}

      {/* Compare Portfolios Modal */}
      {showCompareModal && (
        <div className="feature-modal-overlay" onClick={() => { setShowCompareModal(false); setCompareTarget(null); }}>
          <div className="feature-modal wide" onClick={e => e.stopPropagation()}>
            <div className="feature-modal-header">
              <h3>Compare Portfolios</h3>
              <button className="feature-modal-close" onClick={() => { setShowCompareModal(false); setCompareTarget(null); }}>&times;</button>
            </div>
            <div className="feature-modal-body">
              {/* Save current portfolio */}
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                <input
                  className="feature-modal-input"
                  placeholder="Save current portfolio as..."
                  value={saveName}
                  onChange={e => setSaveName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSavePortfolio()}
                />
                <button className="feature-btn feature-btn-primary" onClick={handleSavePortfolio}>Save</button>
              </div>

              {savedPortfolios.length === 0 && !compareTarget ? (
                <div className="feature-empty">No saved portfolios yet. Save your current portfolio to compare later.</div>
              ) : compareTarget ? (
                /* Side-by-side comparison */
                <div className="feature-compare-grid">
                  <div className="feature-compare-col">
                    <h4>Current</h4>
                    <div className="feature-compare-row"><span>Conviction</span><strong>{analysisResult?.total_conviction?.toFixed(1) ?? '-'}/5</strong></div>
                    <div className="feature-compare-row"><span>Regime</span><strong>{analysisResult?.regime || '-'}</strong></div>
                    <div className="feature-compare-row"><span>Positions</span><strong>{portfolioItems.length}</strong></div>
                    {scores?.slice(0, 5).map(s => (
                      <div key={s.symbol} className="feature-compare-row">
                        <span>{s.symbol}</span><strong>{s.combined_conviction?.toFixed(1)}</strong>
                      </div>
                    ))}
                  </div>
                  <div className="feature-compare-col">
                    <h4>{compareTarget.name}</h4>
                    <div className="feature-compare-row"><span>Conviction</span><strong>{compareTarget.totalConviction?.toFixed(1) ?? '-'}/5</strong></div>
                    <div className="feature-compare-row"><span>Regime</span><strong>{compareTarget.regime || '-'}</strong></div>
                    <div className="feature-compare-row"><span>Positions</span><strong>{compareTarget.positions?.length || 0}</strong></div>
                    {compareTarget.scores?.slice(0, 5).map(s => (
                      <div key={s.symbol} className="feature-compare-row">
                        <span>{s.symbol}</span><strong>{s.conviction?.toFixed(1)}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <ul className="feature-list">
                  {savedPortfolios.map(p => (
                    <li key={p.name} className="feature-list-item" onClick={() => setCompareTarget(p)}>
                      <div>
                        <div className="feature-list-name">{p.name}</div>
                        <div className="feature-list-meta">
                          Conviction: {p.totalConviction?.toFixed(1)}/5 &middot; {p.positions?.length} stocks &middot; {new Date(p.timestamp).toLocaleDateString()}
                        </div>
                      </div>
                      <button className="feature-btn feature-btn-danger" onClick={(e) => handleDeleteSavedPortfolio(p.name, e)}>Delete</button>
                    </li>
                  ))}
                </ul>
              )}
              {compareTarget && (
                <button className="feature-btn feature-btn-secondary" style={{ marginTop: '1rem' }} onClick={() => setCompareTarget(null)}>
                  Back to list
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stock News Modal */}
      {showNewsModal && (
        <div className="feature-modal-overlay" onClick={() => setShowNewsModal(false)}>
          <div className="feature-modal wide" onClick={e => e.stopPropagation()}>
            <div className="feature-modal-header">
              <h3>News for My Stocks</h3>
              <button className="feature-modal-close" onClick={() => setShowNewsModal(false)}>&times;</button>
            </div>
            <div className="feature-modal-body">
              {newsLoading ? (
                <div className="feature-empty">Loading news...</div>
              ) : newsData.length === 0 ? (
                <div className="feature-empty">No news articles found for your portfolio stocks.</div>
              ) : (
                newsData.map((article, i) => (
                  <div key={i} className="feature-news-item">
                    <a className="feature-news-title" href={article.url || article.link || '#'} target="_blank" rel="noopener noreferrer">
                      {article.title || article.headline || 'Untitled'}
                    </a>
                    <div className="feature-news-meta">
                      {article.source || article.site || ''} {article.publishedDate || article.date ? ` \u00b7 ${new Date(article.publishedDate || article.date).toLocaleDateString()}` : ''}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Score History Modal */}
      {showHistoryModal && (
        <div className="feature-modal-overlay" onClick={() => setShowHistoryModal(false)}>
          <div className="feature-modal" onClick={e => e.stopPropagation()}>
            <div className="feature-modal-header">
              <h3>Score History</h3>
              <button className="feature-modal-close" onClick={() => setShowHistoryModal(false)}>&times;</button>
            </div>
            <div className="feature-modal-body">
              {scoreHistory.length === 0 ? (
                <div className="feature-empty">No history yet. Analyze a portfolio to start tracking scores.</div>
              ) : (
                scoreHistory.map(h => (
                  <div key={h.id} className="feature-score-row">
                    <div>
                      <div className="feature-list-name">{new Date(h.timestamp).toLocaleDateString()}</div>
                      <div className="feature-list-meta">{h.positions} stocks &middot; {h.regime || 'Unknown'} regime</div>
                    </div>
                    {/* 0-100, not "/5" with 3.5/1.5 colors (always green). */}
                    <strong style={{ color: convictionColor(h.totalConviction) }}>
                      {h.totalConviction?.toFixed(1)}/100
                    </strong>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Feature Toast */}
      {featureToast && (
        <div className={`feature-toast ${featureToast.type}`}>{featureToast.message}</div>
      )}
    </div>
  );
}

// Floating chat bridge — only shows on Portfolio Analysis page (/portfolio)
function FloatingChatBridge() {
  const { analysisResult, loading } = usePortfolio();
  const { hasFeature } = useTier();
  const location = useLocation();
  const navigate = useNavigate();
  if (location.pathname !== '/portfolio') return null;
  if (!analysisResult || loading) return null;

  if (!hasFeature('ai_chat')) {
    // Show teaser bubble instead of chat
    return (
      <div
        onClick={() => navigate('/pricing')}
        style={{
          position: 'fixed', bottom: '2rem', right: '2rem', zIndex: 900,
          width: 56, height: 56, borderRadius: '50%',
          background: 'linear-gradient(135deg, #6495ED, #00D4AA)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', boxShadow: '0 4px 20px rgba(100,149,237,0.4)',
          transition: 'all 0.2s ease', fontSize: '1.3rem',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
        title="Unlock AI Chat — Pro Plan"
      >
        🤖
      </div>
    );
  }

  return <CopilotChat mode="portfolio" analysisResult={analysisResult} />;
}

// Layout wrapper — hides header/nav on landing page
function AppLayout() {
  const location = useLocation();
  const isLanding = location.pathname === '/';
  // Alpha Workspace is a full-viewport chat surface; the disclaimer footer
  // steals vertical space that the transcript needs.
  const isAlphaWorkspace = location.pathname === '/alpha';
  // Guest guard: logged out, only '/' and OPEN_APP_ROUTES render inside the app.
  // Any other route reached by a client-side hop (a "Try the screener" button
  // on How It Works, a ticker link) does a full load so LoginGate shows the
  // public screener / the sign-up prompt instead of a logged-in page that 401s.
  const guestBlocked = !isAuthenticated() && location.pathname !== '/'
    && !OPEN_APP_ROUTES.some((p) => location.pathname.startsWith(p));
  useEffect(() => {
    if (guestBlocked) window.location.replace(location.pathname + location.search);
  }, [guestBlocked, location.pathname, location.search]);

  return (
    <div className={`App nav-compact ${isAlphaWorkspace ? 'route-alpha' : ''}`}>
      <Onboarding />
      <UpgradeConfirmer />
      {/* Logged-out visitors reach app routes now (/trends, /energy are open),
          so they must never inherit the authed header + Navigation. */}
      {!isLanding && (isAuthenticated() ? (
        <header className="App-header">
          <h1><span className="highlight">Alpha</span>Grade</h1>
          <p>Conviction-based portfolio analysis and stock screening</p>
          <Navigation />
        </header>
      ) : (
        <PublicTopBar />
      ))}

      <RouteTitle />
      <ScrollToTop />

      <main>
        {/* One boundary for every lazy route. The fallback is deliberately
            plain: a spinner that flashes for 80ms reads as jank. */}
        <Suspense fallback={<div className="route-loading" aria-busy="true" />}>
        {guestBlocked ? <div className="route-loading" aria-busy="true" /> : (
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/trends" element={<MarketTrends />} />
          <Route path="/correlations" element={<CorrelationsPage />} />
          <Route path="/research" element={<MarketResearchPage />} />
          <Route path="/screener" element={<StockScreener />} />
          <Route path="/grade-watch" element={<GradeWatchPage />} />
          <Route path="/watchlists" element={<WatchlistsPage />} />
          <Route path="/news" element={<NewsPage />} />
          <Route path="/model-portfolios" element={<ModelPortfoliosPage />} />
          <Route path="/portfolio" element={<PortfolioPage />} />
          <Route path="/energy" element={<MacroEnergy />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/glossary" element={<Glossary />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/how-it-works" element={<HowScoresWork />} />
          <Route path="/alpha/memory" element={<AlphaMemory />} />
          <Route path="/alpha" element={<AlphaWorkspace />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/admin/attribution" element={<AdminAttribution />} />
          <Route path="/admin/social" element={<AdminSocial />} />
          <Route path="/admin/tools" element={<AdminToolsPage />} />
        </Routes>
        )}
        </Suspense>
      </main>

      {!isLanding && !isAlphaWorkspace && (
        <footer className="app-disclaimer-footer">
          <p>This application is for informational purposes only and does not constitute investment advice. Always conduct your own research before making investment decisions.</p>
          <div className="footer-links">
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); window.dispatchEvent(new Event('alphagrade:open-tour')); }}
            >
              Take the tour
            </a>
            <span className="footer-sep">|</span>
            <a href="/faq">Help & FAQ</a>
            <span className="footer-sep">|</span>
            <a href="/contact">Contact / Feedback</a>
            <span className="footer-sep">|</span>
            <a href="/glossary">Glossary</a>
            <span className="footer-sep">|</span>
            <a href="/how-it-works">How Scores Work</a>
            <span className="footer-sep">|</span>
            <a href="/privacy">Privacy</a>
            <span className="footer-sep">|</span>
            <a href="/terms">Terms</a>
          </div>
        </footer>
      )}

      <FloatingChatBridge />
      <LimitReachedModal />
      <InstallPrompt />
    </div>
  );
}

/**
 * Keeps <title> in sync with the route. Renders nothing.
 *
 * Client-side navigation never reloads the document, so without this every
 * route kept index.html's title. Lives inside the Router so useLocation fires
 * on each navigation.
 */
function RouteTitle() {
  const location = useLocation();
  useEffect(() => {
    applyTitle(location.pathname);
  }, [location.pathname]);
  return null;
}

/**
 * ScrollToTop — reset the scroll position on every client-side navigation.
 *
 * React Router does not do this: the document never reloads, so a new route
 * inherits the previous page's scroll offset. On mobile that meant opening
 * Portfolio from part-way down another page dropped you into the MIDDLE of the
 * portfolio, past the heading and the add-a-position controls. Reported from a
 * real phone on 2026-09-11; it affected every route, Portfolio was just where
 * it hurt most.
 *
 * `auto` rather than `smooth` so it is instant and never animates in front of
 * the user on a route change.
 */
function ScrollToTop() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <LoginGate>
      <ThemeProvider>
        <TierProvider>
        <GradeWatchProvider>
        <PortfolioProvider>
          <Router>
            <AppLayout />
          </Router>
        </PortfolioProvider>
        </GradeWatchProvider>
        </TierProvider>
      </ThemeProvider>
    </LoginGate>
  );
}

export default App;
